import { createFileRoute } from "@tanstack/react-router";
import { Link } from "@tanstack/react-router";
import {
  PencilIcon,
  PlusIcon,
  ArrowUpOnSquareIcon,
  XCircleIcon,
} from "@heroicons/react/24/outline";
import { useVocabularyStatistics } from "../../hooks/useVocabularyStatistics";
import { useTextStatistics } from "../../hooks/useTextStatistics";
import { useUserProfileUpdate } from "../../hooks/useUserProfileUpdate";

import useTranslation from "../../hooks/useTranslation";
import { useProfilePicPath } from "../../hooks/useProfilePicPath";
import { useLoginStatus } from "../../hooks/useLoginStatus";
import { useForm } from "react-hook-form";
import { useEffect, useState } from "react";
import { notifySnackbar } from "../../lib/notifySnackbar";

export const Route = createFileRoute("/app/profile")({
  component: UserProfilePage,
});

type ProfileUpdateFormData = {
  pic: FileList;
};

function UserProfilePage() {
  const vocabularyStatistics = useVocabularyStatistics();
  const textStatistics = useTextStatistics();

  const { translate } = useTranslation();
  const loginStatus = useLoginStatus();

  if (!vocabularyStatistics.isSuccess) {
    return <div>loading</div>;
  }

  if (!textStatistics.isSuccess) {
    return <div>loading</div>;
  }

  if (!loginStatus.isSuccess) {
    return <></>;
  }

  if (loginStatus.data.state === "logged out") {
    return <></>;
  }

  return (
    <div className="max-w-4xl mx-auto p-4 space-y-6">
      {/* Profile Header */}
      <div className="flex flex-col md:flex-row items-center md:items-center gap-4 mb-0 md:mb-10">
        <ProfileImageComponent />

        <div className="flex flex-col justify-center h-32 text-center md:text-left">
          <div className="text-sm font-bold text-gray-500">
            @{loginStatus.data.username}
          </div>

          <div className="flex justify-center md:justify-start gap-6 mt-2">
            {[
              { label: "Gefolgt", value: 120 },
              { label: "Follower", value: 430 },
              { label: "Likes", value: 8900 },
            ].map((item) => (
              <div key={item.label}>
                <div className="text-xl font-bold">{item.value}</div>
                <div className="text-sm text-gray-500">{item.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Zielhinweis */}
      <div className="bg-blue-100 border border-blue-300 rounded-xl px-4 py-3 text-sm text-blue-800 shadow-md flex flex-col sm:flex-row sm:justify-between items-start sm:items-center">
        <span className="mb-2 sm:mb-0">
          🎯 Du kannst ein Lernziel festlegen, um Inhalte besser anzupassen.
        </span>

        <span className="w-full flex justify-center sm:w-auto sm:justify-end">
          <Link
            to="/app/learning_goal_dialogue"
            className="text-blue-800 hover:text-blue-700 text-sm font-medium"
          >
            Jetzt festlegen →
          </Link>
        </span>
      </div>

      {/* Bio */}
      <div className="flex items-center justify-center md:justify-start gap-1 mb-1 mt-0">
        <span className="text-gray-700 font-bold text-sm">Über mich</span>
        <PencilIcon className="h-4 w-4 text-gray-700" />
      </div>

      <div className="text-center md:text-left text-gray-700 mt-0.5">
        Sprachen verbinden. Ich lerne täglich neue Wörter und teile meine
        Gedanken mit der Welt. Lass uns gemeinsam wachsen!
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Texte */}
        <div className="rounded-xl p-5 bg-white shadow-sm border">
          <div className="text-sm font-semibold text-gray-500 mb-4">Texte</div>
          <div className="flex justify-between text-center">
            <div className="flex-1">
              <div className="text-2xl font-bold text-gray-800">
                {textStatistics.data.total_created_texts}
              </div>
              <div className="text-sm text-gray-500 mt-1">Total created</div>
            </div>
            <div className="w-px bg-gray-200 mx-4" />
            <div className="flex-1">
              <div className="text-2xl font-bold text-gray-800">
                {textStatistics.data.total_created_texts_length}
              </div>
              <div className="text-sm text-gray-500 mt-1">Total word count</div>
            </div>
          </div>
        </div>

        {/* Vokabeln */}
        <div className="rounded-xl p-5 bg-white shadow-sm border">
          <div className="text-sm font-semibold text-gray-500 mb-4">
            Vokabeln
          </div>
          <div className="flex justify-between text-center">
            <div className="flex-1">
              <div className="text-2xl font-bold text-gray-800">
                {vocabularyStatistics.data.total_created}
              </div>
              <div className="text-sm text-gray-500 mt-1">
                {translate("statistics_total_words_added")}
              </div>
            </div>
            <div className="w-px bg-gray-200 mx-4" />
            <div className="flex-1">
              <div className="text-2xl font-bold text-gray-800">
                {vocabularyStatistics.data.last_four_weeks_created}
              </div>
              <div className="text-sm text-gray-500 mt-1">
                {translate("statistics_total_words_added_last_four_weeks")}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div>
        <div className="flex justify-center md:justify-start gap-4 border-b pb-2">
          <button className="text-blue-600 font-medium flex items-center gap-1">
            <span className="text-sm">📄</span> Öffentliche Texte
          </button>
          <button className="text-gray-500 flex items-center gap-1">
            <span className="text-sm">🔒</span> Private Texte
          </button>
        </div>

        {/* Feed */}
        <div className="space-y-4 mt-4">
          <div className="border rounded-lg p-4 shadow-sm">
            <div className="text-sm text-gray-500 mb-1">
              Sprachlevel: A2 · Sprache: Deutsch
            </div>
            <div className="text-gray-800">
              Heute habe ich über meine Lieblingsfilme geschrieben. Was sind
              deine?
            </div>
          </div>
          <div className="border rounded-lg p-4 shadow-sm">
            <div className="text-sm text-gray-500 mb-1">
              Sprachlevel: B1 · Sprache: Spanisch
            </div>
            <div className="text-gray-800">
              La cultura es una ventana al alma de una nación.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ProfileImageComponent() {
  const profileImage = useProfilePicPath();
  const updateProfile = useUserProfileUpdate();

  const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(null);

  const { register, handleSubmit, watch, reset } =
    useForm<ProfileUpdateFormData>();
  const formFileList = watch("pic");
  const selectedImage = formFileList?.[0];
  const showSubmit = !!selectedImage;

  useEffect(() => {
    if (selectedImage) {
      const url = URL.createObjectURL(selectedImage);
      setImagePreviewUrl(url);
      return () => URL.revokeObjectURL(url);
    } else {
      setImagePreviewUrl(null);
    }
  }, [selectedImage]);

  return (
    <form
      onSubmit={handleSubmit(async (data) => {
        try {
          const pic = data.pic[0];
          await updateProfile.mutateAsync({
            updateUserProfileDto: {
              pic,
            },
          });
          notifySnackbar({ message: "changed image." });
          reset();
        } catch {
          notifySnackbar({
            message: "Something went wrong changing the image!",
          });
        }
      })}
    >
      <div className="relative">
        <img
          src={imagePreviewUrl ?? profileImage()}
          alt="profile image"
          className="w-32 h-32 rounded-full object-fill"
        />
        <div className="absolute bottom-0 right-0 bg-blue-400 border-3 border-white p-1 rounded-full shadow cursor-pointer">
          <label className={showSubmit ? "hidden" : undefined}>
            <span className="text-sm">
              <PlusIcon className="h-5 w-5 text-white cursor-pointer" />
            </span>
            <input
              type="file"
              accept="image/*"
              id="pic"
              {...register("pic", {
                required: true,
              })}
              className="hidden"
            />
          </label>
          <label className={showSubmit ? undefined : "hidden"}>
            <span className="text-sm">
              <ArrowUpOnSquareIcon className="h-5 w-5 text-white cursor-pointer" />
            </span>
            <button type="submit" className="hidden" />
          </label>
        </div>
        <div
          className={
            "absolute bottom-0 left-0 bg-blue-400 border-3 border-white p-1 rounded-full shadow cursor-pointer " +
            (showSubmit ? undefined : "hidden")
          }
        >
          <label>
            <span className="text-sm">
              <XCircleIcon className="h-5 w-5 text-white cursor-pointer" />
            </span>
            <button type="button" onClick={() => reset()} className="hidden" />
          </label>
        </div>
      </div>
    </form>
  );
}
